import { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundaryComponent } from 'ErrorBoundary';
import { posthog } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { BugsnagClient } from 'utils/Bugsnag';
import { initializeFullstory } from 'utils/Fullstory';
import { initializeGoogleAnalytics } from 'utils/GoogleAnalytics';
import { handleAccessToken, injectStore } from 'utils/http-client';
import App from './App';
import './assets/fonts/fontello/css/saf.css';
import { persistor, store } from './redux/store';
import * as serviceWorker from './serviceWorker';
import { initializePostHog } from './utils/Posthog';

// Populate using values from NerdGraph
const options = {
  info: {
    applicationID: '1120388570',
    beacon: 'bam.nr-data.net',
    errorBeacon: 'bam.nr-data.net',
    licenseKey: 'NRJS-91b207c9b4fe7834571',
    sa: 1,
  },
  init: {
    ajax: {
      deny_list: ['bam.nr-data.net'],
    },
    distributed_tracing: {
      enabled: true,
    },
    privacy: {
      cookies_enabled: true,
    },
    session_replay: {
      autoStart: true,
      block_selector: '',
      collect_fonts: true,
      enabled: true,
      error_sampling_rate: 100,
      fix_stylesheets: true,
      inline_images: false,
      inline_stylesheet: true,
      mask_all_inputs: true,
      mask_input_options: {},
      mask_text_selector: '*',
      preload: false,
      sampling_rate: 25,
    },
  },
  loader_config: {
    accountID: 6579482,
    agentID: 1120388570,
    applicationID: 1120388570,
    licenseKey: 'NRJS-91b207c9b4fe7834571',
    trustKey: 6579482,
  },
};

// The agent loader code executes immediately on instantiation.
if (process.env.NODE_ENV === 'production') {
  new BrowserAgent(options);
}

const queryClient = new QueryClient();

const ErrorBoundary: BugsnagErrorBoundary & { children?: React.ReactNode } =
  BugsnagClient.getPlugin('react')!.createErrorBoundary(React);

const container = document.getElementById('root');

initializeGoogleAnalytics();
initializePostHog();
initializeFullstory();

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <ErrorBoundary FallbackComponent={ErrorBoundaryComponent}>
        <QueryClientProvider client={queryClient}>
          <PersistGate persistor={persistor}>
            <PostHogProvider client={posthog}>
              <App />
            </PostHogProvider>
          </PersistGate>
        </QueryClientProvider>
      </ErrorBoundary>
    </Provider>
  </React.StrictMode>,
  container,
);

handleAccessToken();
injectStore(store);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA

// serviceWorker.register({
//   onSuccess: ({ registration, serviceWorkerActive }) => {
//     console.log({ success_sw_reg: registration, serviceWorkerActive });
//     store.dispatch(initializeServiceWorker({ registration, serviceWorkerActive }));
//   },
//   onUpdate: (registration) => {
//     console.log({ update_sw_reg: registration });
//     store.dispatch(updateServiceWorker(registration));
//   },
// });
serviceWorker.unregister();
